import { useState, useCallback, useEffect, useRef } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import { useApi, configApiRef, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import TuneIcon from '@material-ui/icons/Tune';
import DescriptionIcon from '@material-ui/icons/Description';
import TimelineIcon from '@material-ui/icons/Timeline';

const K8S_NAMESPACE_ANNOTATION  = 'backstage.io/kubernetes-namespace';
const K8S_NAMESPACES_ANNOTATION = 'backstage.io/kubernetes-namespaces';
const HUMANITEC_ORG_ANNOTATION   = 'humanitec.dev/orgId';
const HUMANITEC_PROJECT_ANNOTATION = 'humanitec.dev/projectId';

export const isK8sOperationsAvailable = (entity: any) =>
  Boolean(
    entity?.metadata?.annotations?.[K8S_NAMESPACE_ANNOTATION] ||
    entity?.metadata?.annotations?.[K8S_NAMESPACES_ANNOTATION],
  );

type ActionResult = { ok: true; message: string; detail?: string } | { ok: false; message: string };

type GraphContainer = {
  name: string; ready: boolean; restartCount: number; image: string; state: string;
};
type GraphPod = {
  name: string; phase: string; podIP: string | null; ready: boolean;
  restarts: number; startTime: string | null; node: string | null;
  containers: GraphContainer[];
};
type GraphVolume = { name: string; type: string; refName: string | null };
type GraphDeployment = {
  name: string; namespace: string; replicas: number; readyReplicas: number;
  strategy: string; serviceAccountName: string;
  specContainers: { name: string; image: string; ports: string[] }[];
  volumes: GraphVolume[];
};
type GraphService = { name: string; type: string; clusterIP: string | null; ports: string[] };
type GraphData = { deployment: GraphDeployment; pods: GraphPod[]; services: GraphService[] };

type EventSummary = {
  type: string; reason: string; message: string;
  count: number; lastTimestamp: string | undefined;
};

type GraphIngress = {
  name: string; className: string | null; tls: boolean;
  rules: { host: string; paths: string[] }[];
  backendServices?: string[];
};
type GraphNode = { name: string; status: string; roles: string[]; cpu: string; memory: string };
type GraphStorageClass = { name: string; provisioner: string; isDefault: boolean };
type GraphPV  = { name: string; capacity: string; storageClass: string; status: string; claimRef: string | null };
type GraphPVC = { name: string; storageClass: string; capacity: string; accessModes: string[]; status: string; volumeName: string | null };
type NamespaceGraphData = { name: string; graph: GraphData | null; ingresses: GraphIngress[]; pvcs: GraphPVC[] };
type MultiNamespaceGraphData = {
  namespaces: NamespaceGraphData[];
  cluster: { nodes: GraphNode[]; storageClasses: GraphStorageClass[]; persistentVolumes: GraphPV[] };
};
type HumanitecEnvSummary = { id: string; displayName: string; status: string; resourceTypes: string[] };

type NodeInfoField = { label: string; value: string; mono?: boolean };
type NodeInfo = { kind: string; name: string; fields: NodeInfoField[] };

const useStyles = makeStyles(theme => ({
  actionCell: { display: 'flex', gap: theme.spacing(1), alignItems: 'center', flexWrap: 'wrap' },
  resultText: {
    fontSize: '0.78rem', marginTop: theme.spacing(0.5), padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius, backgroundColor: theme.palette.background.default,
    fontFamily: 'monospace', wordBreak: 'break-all',
  },
  errorText: { color: theme.palette.error.main, fontSize: '0.78rem', marginTop: theme.spacing(0.5) },
  slider: { flex: 1 },
  replicaBadge: { minWidth: 28, textAlign: 'center', fontWeight: 700 },
}));

// ── Inline K8s resource type icons ─────────────────────────────────────────────
// Each renders into an SVG context at (x, y) in a size×size bounding box.
function KubeIcon({ type, x, y, size = 20, clr }: {
  type: 'Deployment' | 'Pod' | 'Service' | 'Ingress' | 'ServiceAccount' | 'Node' | 'StorageClass' | 'PersistentVolume';
  x: number; y: number; size?: number; clr: string;
}) {
  const h  = size / 2;
  const cx = x + h; const cy = y + h;
  const sw = 1.4;
  if (type === 'Deployment') {
    // Hexagon outline with 3 inner dots (workload managing N pods)
    const r   = h - 1.5;
    const pts = Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
    }).join(' ');
    return (
      <g>
        <polygon points={pts} fill="none" stroke={clr} strokeWidth={sw} />
        <circle cx={cx}              cy={y + size * 0.28} r={1.8} fill={clr} />
        <circle cx={x + size * 0.24} cy={y + size * 0.68} r={1.8} fill={clr} />
        <circle cx={x + size * 0.76} cy={y + size * 0.68} r={1.8} fill={clr} />
      </g>
    );
  }
  if (type === 'Pod') {
    // Concentric circles — running container inside a pod
    return (
      <g>
        <circle cx={cx} cy={cy} r={h - 1.5}    fill="none" stroke={clr} strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={h * 0.45}   fill="none" stroke={clr} strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={1.8}         fill={clr} />
      </g>
    );
  }
  if (type === 'Service') {
    // Diamond — routing / load balancing
    return (
      <polygon
        points={`${cx},${y + 1.5} ${x + size - 1.5},${cy} ${cx},${y + size - 1.5} ${x + 1.5},${cy}`}
        fill="none" stroke={clr} strokeWidth={sw}
      />
    );
  }
  if (type === 'Ingress') {
    // Rectangle with inbound arrow — external traffic entering
    const bx = x + size * 0.18; const by = y + size * 0.22;
    const bw = size * 0.64;     const bh = size * 0.56;
    const ay = cy;
    return (
      <g>
        <rect x={bx} y={by} width={bw} height={bh} rx={2} fill="none" stroke={clr} strokeWidth={sw} />
        <path
          d={`M${x + size * 0.36},${ay} L${x + size * 0.65},${ay} M${x + size * 0.55},${ay - 3.5} L${x + size * 0.65},${ay} L${x + size * 0.55},${ay + 3.5}`}
          fill="none" stroke={clr} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    );
  }
  if (type === 'ServiceAccount') {
    // Person silhouette — identity / RBAC
    const hr  = size * 0.19;
    const hcy = y + size * 0.33;
    return (
      <g>
        <circle cx={cx} cy={hcy} r={hr} fill="none" stroke={clr} strokeWidth={sw} />
        <path
          d={`M${x + size * 0.1},${y + size - 1.5} Q${cx},${y + size * 0.56} ${x + size * 0.9},${y + size - 1.5}`}
          fill="none" stroke={clr} strokeWidth={sw} strokeLinecap="round"
        />
      </g>
    );
  }
  if (type === 'Node') {
    const bw = size * 0.72; const bh = size * 0.28;
    const bx = x + (size - bw) / 2;
    return (
      <g>
        <rect x={bx} y={y + size * 0.10} width={bw} height={bh} rx={2} fill="none" stroke={clr} strokeWidth={sw} />
        <rect x={bx} y={y + size * 0.62} width={bw} height={bh} rx={2} fill="none" stroke={clr} strokeWidth={sw} />
        <circle cx={bx + bw - 5} cy={y + size * 0.10 + bh / 2} r={2} fill={clr} />
        <circle cx={bx + bw - 5} cy={y + size * 0.62 + bh / 2} r={2} fill={clr} />
      </g>
    );
  }
  if (type === 'StorageClass') {
    const ew = size * 0.68;
    const ex = x + (size - ew) / 2;
    return (
      <g>
        <ellipse cx={cx} cy={y + size * 0.72} rx={ew / 2} ry={size * 0.10} fill="none" stroke={clr} strokeWidth={sw} />
        <ellipse cx={cx} cy={y + size * 0.44} rx={ew / 2} ry={size * 0.10} fill="none" stroke={clr} strokeWidth={sw} />
        <line x1={ex}      y1={y + size * 0.44} x2={ex}      y2={y + size * 0.72} stroke={clr} strokeWidth={sw} />
        <line x1={ex + ew} y1={y + size * 0.44} x2={ex + ew} y2={y + size * 0.72} stroke={clr} strokeWidth={sw} />
      </g>
    );
  }
  if (type === 'PersistentVolume') {
    const ew = size * 0.64;
    const ex = x + (size - ew) / 2;
    const ty = y + size * 0.20; const by2 = y + size * 0.80;
    return (
      <g>
        <ellipse cx={cx} cy={ty}  rx={ew / 2} ry={size * 0.11} fill="none" stroke={clr} strokeWidth={sw} />
        <ellipse cx={cx} cy={by2} rx={ew / 2} ry={size * 0.11} fill="none" stroke={clr} strokeWidth={sw} />
        <line x1={ex}      y1={ty} x2={ex}      y2={by2} stroke={clr} strokeWidth={sw} />
        <line x1={ex + ew} y1={ty} x2={ex + ew} y2={by2} stroke={clr} strokeWidth={sw} />
      </g>
    );
  }
  return null;
}

// ── Cluster resource bar (SVG-style node/SC/PV cards) ──────────────────────────
function K8sClusterBar({
  nodes, storageClasses, persistentVolumes, loading, embedOffset,
}: {
  nodes: GraphNode[];
  storageClasses: GraphStorageClass[];
  persistentVolumes: GraphPV[];
  loading?: boolean;
  embedOffset?: { x: number; y: number };
}) {
  const theme   = useTheme();
  const primary = theme.palette.primary.main;
  const divClr  = theme.palette.divider;
  const txtMain = theme.palette.text.primary;
  const txtSub  = theme.palette.text.secondary;
  const bgPaper = theme.palette.background.paper;
  const clrOk      = theme.palette.success?.main ?? '#4caf50';
  const clrError   = theme.palette.error.main;
  const clrWarning = theme.palette.warning?.main ?? '#ff9800';

  const CW = 160; const CH = 100; const CGAP = 10;
  const OPAD = 14; const CY = 12;
  const STRIP = 6; const IP = 10;
  const sw = 1.4;
  const rowH = CH + CY * 2;

  const trunc = (s: string, n: number) => s.length > n ? s.slice(0, n - 1) + '\u2026' : s;
  const nodeTail = (name: string) => {
    const base = name.split('.')[0];
    const tail = base.split('-').pop() ?? base;
    return tail.length <= 14 ? tail : '\u2026' + tail.slice(-12);
  };
  const shortProv = (p: string) => p
    .replace('disk.csi.azure.com', 'azure/disk')
    .replace('file.csi.azure.com', 'azure/file')
    .replace('blob.csi.azure.com', 'azure/blob')
    .replace('.csi.azure.com', '/azure')
    .replace('kubernetes.io/', 'k8s/');
  const fmtMemory = (mem: string) => {
    if (mem.endsWith('Ki')) {
      const ki = parseInt(mem.slice(0, -2), 10);
      if (ki >= 1024 * 1024) return (ki / 1024 / 1024).toFixed(1) + 'Gi';
      if (ki >= 1024)        return Math.round(ki / 1024) + 'Mi';
    }
    if (mem.endsWith('Gi') || mem.endsWith('Mi')) return mem;
    return mem;
  };

  const renderNode = (n: GraphNode, cx: number) => {
    const cy = CY;
    const ready = n.status === 'Ready';
    const clr = ready ? clrOk : clrError;
    const isCP = n.roles.includes('control-plane') || n.roles.includes('master');
    return (
      <g key={'nd-' + n.name}>
        <rect x={cx} y={cy} width={CW} height={CH} rx={6} fill={bgPaper} stroke={clr + '55'} strokeWidth={sw} />
        <rect x={cx} y={cy} width={CW} height={STRIP} rx={3} fill={clr + 'aa'} />
        <rect x={cx} y={cy + 3} width={CW} height={STRIP - 3} fill={clr + 'aa'} />
        <KubeIcon type="Node" x={cx + IP} y={cy + STRIP + 5} size={18} clr={clr} />
        <text x={cx + IP + 22} y={cy + STRIP + 14} fontSize={9} fontFamily="monospace" fontWeight={600}
          fill={txtSub} opacity={0.6} letterSpacing={0.4}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {isCP ? 'CTRL-PLANE' : 'NODE'}
        </text>
        <circle cx={cx + CW - 10} cy={cy + STRIP + 14} r={3.5} fill={clr} opacity={0.9} />
        <text x={cx + IP} y={cy + 47} fontSize={11.5} fontFamily="monospace" fontWeight={600} fill={txtMain}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(nodeTail(n.name), 14)}
        </text>
        <text x={cx + IP} y={cy + 66} fontSize={9} fontFamily="monospace" fill={txtSub}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(n.cpu + ' \u00b7 ' + fmtMemory(n.memory), 20)}
        </text>
        <text x={cx + IP} y={cy + 84} fontSize={8.5} fontFamily="monospace" fill={clr} opacity={0.8}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {n.status}
        </text>
      </g>
    );
  };

  const renderSC = (sc: GraphStorageClass, cx: number) => {
    const cy = CY;
    const iclr = sc.isDefault ? primary : txtSub;
    return (
      <g key={'sc-' + sc.name}>
        <rect x={cx} y={cy} width={CW} height={CH} rx={6} fill={bgPaper}
          stroke={sc.isDefault ? primary + '44' : divClr} strokeWidth={sw} />
        <rect x={cx} y={cy} width={CW} height={STRIP} rx={3}
          fill={sc.isDefault ? primary + 'bb' : divClr} />
        <rect x={cx} y={cy + 3} width={CW} height={STRIP - 3}
          fill={sc.isDefault ? primary + 'bb' : divClr} />
        <KubeIcon type="StorageClass" x={cx + IP} y={cy + STRIP + 5} size={18} clr={iclr} />
        <text x={cx + IP + 22} y={cy + STRIP + 14} fontSize={9} fontFamily="monospace" fontWeight={600}
          fill={txtSub} opacity={0.6} letterSpacing={0.4}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          SC
        </text>
        {sc.isDefault && (
          <text x={cx + CW - IP} y={cy + STRIP + 14} fontSize={8.5} fontFamily="monospace"
            fill={primary} textAnchor="end" fontWeight={600}
            style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
            default
          </text>
        )}
        <text x={cx + IP} y={cy + 47} fontSize={11.5} fontFamily="monospace" fontWeight={600} fill={txtMain}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(sc.name, 14)}
        </text>
        <text x={cx + IP} y={cy + 66} fontSize={9} fontFamily="monospace" fill={txtSub}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(shortProv(sc.provisioner), 20)}
        </text>
      </g>
    );
  };

  const renderPV = (pv: GraphPV, cx: number) => {
    const cy = CY;
    const bound = pv.status === 'Bound';
    const clr = bound ? clrOk : clrWarning;
    const claim = pv.claimRef ? (pv.claimRef.split('/').pop() ?? pv.claimRef) : null;
    return (
      <g key={'pv-' + pv.name}>
        <rect x={cx} y={cy} width={CW} height={CH} rx={6} fill={bgPaper} stroke={divClr} strokeWidth={sw} />
        <rect x={cx} y={cy} width={CW} height={STRIP} rx={3} fill={clr + '55'} />
        <rect x={cx} y={cy + 3} width={CW} height={STRIP - 3} fill={clr + '55'} />
        <KubeIcon type="PersistentVolume" x={cx + IP} y={cy + STRIP + 5} size={18} clr={txtSub} />
        <text x={cx + IP + 22} y={cy + STRIP + 14} fontSize={9} fontFamily="monospace" fontWeight={600}
          fill={txtSub} opacity={0.6} letterSpacing={0.4}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          PV
        </text>
        <circle cx={cx + CW - 10} cy={cy + STRIP + 14} r={3.5} fill={clr} opacity={0.9} />
        <text x={cx + IP} y={cy + 47} fontSize={11.5} fontFamily="monospace" fontWeight={600} fill={txtMain}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(pv.capacity, 12)}
        </text>
        <text x={cx + IP} y={cy + 66} fontSize={9} fontFamily="monospace" fill={txtSub}
          style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
          {trunc(pv.storageClass, 20)}
        </text>
        {claim && (
          <text x={cx + IP} y={cy + 84} fontSize={8.5} fontFamily="monospace" fill={clr} opacity={0.8}
            style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
            {trunc(claim, 20)}
          </text>
        )}
      </g>
    );
  };

  // ── Row 1: Nodes ────────────────────────────────────────────────────────
  let nx = OPAD;
  const nodeElems = nodes.map(n => { const el = renderNode(n, nx); nx += CW + CGAP; return el; });
  const nodeRowW  = Math.max(nx + OPAD - CGAP, 240);

  // ── Row 2: StorageClasses + PVs ─────────────────────────────────────────
  let sx = OPAD;
  const storeElems: React.ReactNode[] = [];
  storageClasses.forEach(sc => { storeElems.push(renderSC(sc, sx)); sx += CW + CGAP; });
  if (storageClasses.length > 0 && persistentVolumes.length > 0) {
    const dx = sx + 6;
    storeElems.push(
      <line key="pv-div" x1={dx} y1={CY + 4} x2={dx} y2={CY + CH - 4}
        stroke={divClr} strokeWidth={1} opacity={0.5} />,
    );
    sx += 20;
  }
  persistentVolumes.slice(0, 8).forEach(pv => { storeElems.push(renderPV(pv, sx)); sx += CW + CGAP; });
  const storeRowW = Math.max(sx + OPAD - CGAP, 240);

  const hasNodes   = nodes.length > 0;
  const hasStorage = storageClasses.length > 0 || persistentVolumes.length > 0;

  // ── Embed mode: return a <g> for use inside a parent <svg> (no Box/scroll) ──
  if (embedOffset) {
    if (!hasNodes && !hasStorage) return null;
    const ROW_LW = 48;
    const storeY = hasNodes ? rowH + 1 : 0;
    const embedW = ROW_LW + Math.max(nodeRowW, storeRowW);
    return (
      <g transform={`translate(${embedOffset.x},${embedOffset.y})`}>
        {hasNodes && (
          <g>
            <text x={ROW_LW / 2} y={rowH / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={7.5} fontFamily="monospace" fontWeight={700}
              fill={txtSub} opacity={0.4} letterSpacing={1}
              transform={`rotate(-90,${ROW_LW / 2},${rowH / 2})`}
              style={{ userSelect: 'none' }}>NODES</text>
            <line x1={ROW_LW} y1={0} x2={ROW_LW} y2={rowH}
              stroke={divClr} strokeWidth={1} opacity={0.5} />
            {hasStorage && (
              <line x1={0} y1={rowH} x2={embedW} y2={rowH}
                stroke={divClr} strokeWidth={1} opacity={0.6} />
            )}
            <g transform={`translate(${ROW_LW},0)`}>{nodeElems}</g>
          </g>
        )}
        {hasStorage && (
          <g>
            <text x={ROW_LW / 2} y={storeY + rowH / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={7.5} fontFamily="monospace" fontWeight={700}
              fill={txtSub} opacity={0.4} letterSpacing={1}
              transform={`rotate(-90,${ROW_LW / 2},${storeY + rowH / 2})`}
              style={{ userSelect: 'none' }}>STORAGE</text>
            <line x1={ROW_LW} y1={storeY} x2={ROW_LW} y2={storeY + rowH}
              stroke={divClr} strokeWidth={1} opacity={0.5} />
            <g transform={`translate(${ROW_LW},${storeY})`}>{storeElems}</g>
          </g>
        )}
      </g>
    );
  }

  if (!loading && !hasNodes && !hasStorage) {
    return (
      <Box style={{ padding: '0 12px', height: rowH, display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" style={{ opacity: 0.38, fontStyle: 'italic' }}>
          cluster-scoped resources unavailable &mdash; add ClusterRole to backstage-sa
        </Typography>
      </Box>
    );
  }

  const rowLabelStyle: React.CSSProperties = {
    width: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRight: '1px solid ' + divClr, flexShrink: 0,
  };
  const rowLabelTextStyle: React.CSSProperties = {
    fontFamily: 'monospace', fontWeight: 700, fontSize: '0.62rem',
    opacity: 0.4, letterSpacing: '0.1em', textTransform: 'uppercase',
    writingMode: 'vertical-rl', transform: 'rotate(180deg)',
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>
      {hasNodes && (
        <Box style={{ display: 'flex', alignItems: 'stretch', borderBottom: hasStorage ? '1px solid ' + divClr : undefined }}>
          <Box style={rowLabelStyle}>
            <Typography style={rowLabelTextStyle}>nodes</Typography>
          </Box>
          <Box style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}>
            <svg width={nodeRowW} height={rowH} style={{ display: 'block' }}>{nodeElems}</svg>
          </Box>
        </Box>
      )}
      {hasStorage && (
        <Box style={{ display: 'flex', alignItems: 'stretch' }}>
          <Box style={rowLabelStyle}>
            <Typography style={rowLabelTextStyle}>storage</Typography>
          </Box>
          <Box style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden' }}>
            <svg width={storeRowW} height={rowH} style={{ display: 'block' }}>{storeElems}</svg>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── SVG Topology Diagram ──────────────────────────────────────────────────────

// ── Manifest config icon — small circle with 3 horizontal lines ─────────────
function ManifestIcon({ cx, cy, onClick, clr = '#4caf50' }: { cx: number; cy: number; onClick: () => void; clr?: string }) {
  return (
    <g style={{ cursor: 'pointer' }} onClick={onClick}>
      <circle cx={cx} cy={cy} r={9} fill={clr + '22'} stroke={clr} strokeWidth={1.2} />
      <line x1={cx - 4} y1={cy - 3} x2={cx + 4} y2={cy - 3} stroke={clr} strokeWidth={1.3} strokeLinecap="round" />
      <line x1={cx - 4} y1={cy}     x2={cx + 4} y2={cy}     stroke={clr} strokeWidth={1.3} strokeLinecap="round" />
      <line x1={cx - 4} y1={cy + 3} x2={cx + 2} y2={cy + 3} stroke={clr} strokeWidth={1.3} strokeLinecap="round" />
    </g>
  );
}

// Pure layout computation — shared between K8sGraphDiagram and K8sMultiNsGraph
function calcNsDiagramDims(
  graph: GraphData | null,
  ingresses: GraphIngress[],
  pvcs: GraphPVC[] = [],
): { svgW: number; svgH: number; pvcRowY: number; pvcStartX: number } {
  if (!graph) return { svgW: 640, svgH: 220, pvcRowY: -1, pvcStartX: 0 };
  const PAD = 28; const H_JOIN = 64;
  const DW = 320; const DH = 148;
  const SW = 220;
  const SAW = 154;
  const PW = 300; const PH_GAP = 32;
  const P_HDR = 102; const C_H = 36; const V_H = 26;
  const P_PAD = 16; const LOGS_ROW = 32; const V_SEP = 88;
  const NS_PAD = 14; const NS_LABEL_H = 22;
  const PVCW = 140; const PVCH = 52; const PVCGAP = 12; const PVC_SEP = 30;
  const { deployment, pods } = graph;
  const hasVols = deployment.volumes.length > 0;
  const _podH = (pod: GraphPod) => {
    const ctrs = pod.containers.length > 0 ? pod.containers.length : Math.max(deployment.specContainers.length, 1);
    return P_HDR + ctrs * C_H + (hasVols ? V_H : 0) + LOGS_ROW + P_PAD;
  };
  const nPods    = Math.max(pods.length, 1);
  const podsRowW = nPods * PW + (nPods - 1) * PH_GAP;
  const nSvcs    = graph.services.length;
  const svcSecW  = nSvcs > 0 ? SW + H_JOIN : 0;
  const nIngs    = ingresses.length;
  const ingSecW  = nIngs > 0 ? SW + H_JOIN : 0;
  const mid      = Math.max(DW, podsRowW);
  const svgW     = PAD + ingSecW + svcSecW + mid + SAW + H_JOIN + PAD;
  const deployY  = PAD + NS_LABEL_H + NS_PAD;
  const deployX  = PAD + ingSecW + svcSecW + Math.floor((mid - DW) / 2);
  const podsY    = deployY + DH + V_SEP;
  const maxPodH  = pods.length > 0 ? Math.max(...pods.map(p => _podH(p))) : P_HDR + LOGS_ROW + P_PAD;
  const hasPvcs  = pvcs.length > 0;
  const pvcTotalW = pvcs.length * PVCW + Math.max(0, pvcs.length - 1) * PVCGAP;
  const pvcStartX = deployX + Math.floor((DW - pvcTotalW) / 2);
  const pvcRowY  = hasPvcs ? podsY + maxPodH + PVC_SEP : -1;
  const svgH     = hasPvcs ? pvcRowY + PVCH + PAD : podsY + maxPodH + PAD;
  return { svgW, svgH, pvcRowY, pvcStartX };
}

function K8sGraphDiagram({
  graphData, onPodClick, onRestartClick, onEventsClick, onScaleClick, onNodeInfo,
  onManifestClick,
  ingresses = [],
  pvcs = [],
  embedOffset,
}: {
  graphData: GraphData;
  onPodClick: (pod: GraphPod) => void;
  onRestartClick: () => void;
  onEventsClick: () => void;
  onScaleClick: () => void;
  onNodeInfo: (info: NodeInfo) => void;
  onManifestClick?: (kind: string, name: string, namespace: string) => void;
  ingresses?: GraphIngress[];
  pvcs?: GraphPVC[];
  embedOffset?: { x: number; y: number };
}) {
  const theme     = useTheme();
  const primary   = theme.palette.primary.main;
  const divClr    = theme.palette.divider;
  const txtMain   = theme.palette.text.primary;
  const txtSub    = theme.palette.text.secondary;
  const bgPaper   = theme.palette.background.paper;
  const bgDefault = theme.palette.background.default;
  const clrOk      = theme.palette.success?.main ?? '#4caf50';
  const clrError   = theme.palette.error.main;
  const clrWarning = theme.palette.warning?.main ?? '#ff9800';
  const clrMuted   = theme.palette.grey?.[500] ?? '#9e9e9e';
  const clrInfo    = theme.palette.info?.main ?? '#2196f3';
  const clrSecret  = theme.palette.secondary.main;
  const clrStorage = theme.palette.info?.light ?? '#00bcd4';

  // Layout constants — scaled up for readability
  const PAD = 28; const H_JOIN = 64;
  const DW = 320; const DH = 148;
  const SW = 220; const SH = 90; const SV_GAP = 12;
  const SAW = 154; const SAH = 52;
  const PW = 300; const PH_GAP = 32;
  const P_STRIP = 7; const P_HDR = 102;
  const C_H = 36; const V_H = 26;
  const P_PAD = 16; const LOGS_ROW = 32;
  const V_SEP = 88;
  const VOL_CW = 64; const VOL_CH = 18;

  const trunc = (s: string | null | undefined, n: number) =>
    s && s.length > n ? s.slice(0, n - 1) + '\u2026' : (s ?? '');

  const shortImage = (img: string) => {
    if (!img) return '\u2014';
    const noTag = img.split(':')[0];
    const tag   = img.includes(':') ? img.slice(img.indexOf(':') + 1) : 'latest';
    const base  = noTag.split('/').pop() ?? noTag;
    const shortTag = tag.length > 9 ? tag.slice(0, 8) + '\u2026' : tag;
    return trunc(base + ':' + shortTag, 28);
  };

  const phaseColor = (p: string) => {
    switch ((p ?? '').toLowerCase()) {
      case 'running': case 'succeeded': return clrOk;
      case 'pending': return clrWarning;
      case 'failed':  return clrError;
      default:        return clrMuted;
    }
  };

  const ctrStateColor = (state: string, ready: boolean) => {
    if (state === 'running')    return ready ? clrOk : clrWarning;
    if (state === 'waiting')    return clrWarning;
    if (state === 'terminated') return clrMuted;
    return clrMuted;
  };

  const svcTypeColor = (t: string) =>
    t === 'LoadBalancer' ? clrOk : t === 'NodePort' ? clrWarning : primary;

  const volStyle = (v: GraphVolume) => {
    const abbrs: Record<string, string> = {
      configMap: 'CM', secret: 'SK', pvc: 'PV', emptyDir: 'ED', projected: 'PR',
    };
    const bgs:   Record<string, string> = {
      configMap: clrInfo + '22', secret: clrSecret + '22', pvc: clrStorage + '22',
    };
    const fgs:   Record<string, string> = {
      configMap: clrInfo, secret: clrSecret, pvc: clrStorage,
    };
    return {
      abbr:  abbrs[v.type] ?? '??',
      bg:    bgs[v.type]   ?? clrMuted + '22',
      fg:    fgs[v.type]   ?? clrMuted,
      label: trunc(v.refName ?? v.name, 10),
    };
  };

  const { deployment, pods, services } = graphData;
  const hasVols = deployment.volumes.length > 0;

  // Pod card height — Open Logs pill at the bottom is the click target
  const podH = (pod: GraphPod) => {
    const ctrs = pod.containers.length > 0
      ? pod.containers.length
      : Math.max(deployment.specContainers.length, 1);
    return P_HDR + ctrs * C_H + (hasVols ? V_H : 0) + LOGS_ROW + P_PAD;
  };

  const nPods      = Math.max(pods.length, 1);
  const podsRowW   = nPods * PW + (nPods - 1) * PH_GAP;
  const nSvcs      = services.length;
  const svcSecW    = nSvcs > 0 ? SW + H_JOIN : 0;
  const nIngs      = ingresses.length;
  const ingSecW    = nIngs > 0 ? SW + H_JOIN : 0;
  const mid        = Math.max(DW, podsRowW);

  const NS_PAD      = 14;  // padding inside namespace boundary box
  const NS_LABEL_H  = 22;  // height of namespace label bar at top of boundary

  const svgW       = PAD + ingSecW + svcSecW + mid + SAW + H_JOIN + PAD;
  const deployX    = PAD + ingSecW + svcSecW + Math.floor((mid - DW) / 2);
  const deployY    = PAD + NS_LABEL_H + NS_PAD;  // pushed down to make room for NS label
  const deployCX   = deployX + DW / 2;
  const deployCY   = deployY + DH / 2;

  const podsStartX = PAD + ingSecW + svcSecW + Math.floor((mid - podsRowW) / 2);
  const podsY      = deployY + DH + V_SEP;

  const svcsTotalH = nSvcs * SH + Math.max(0, nSvcs - 1) * SV_GAP;
  const svcsGroupY = deployY + (DH - svcsTotalH) / 2;
  const svcsX      = deployX - H_JOIN - SW;

  const ingTotalH  = nIngs * SH + Math.max(0, nIngs - 1) * SV_GAP;
  const ingGroupY  = deployY + (DH - ingTotalH) / 2;
  const ingX       = svcsX - H_JOIN - SW;

  const saX = deployX + DW + H_JOIN;
  const saY = deployY + Math.floor((DH - SAH) / 2);

  const maxPodH = pods.length > 0 ? Math.max(...pods.map(p => podH(p))) : P_HDR + LOGS_ROW + P_PAD;

  // PVC layout — persistent volume claims bound to this deployment
  const PVCW = 140; const PVCH = 52; const PVCGAP = 12; const PVC_SEP = 30;
  const hasPvcs    = pvcs.length > 0;
  const pvcTotalW  = pvcs.length * PVCW + Math.max(0, pvcs.length - 1) * PVCGAP;
  const pvcStartX  = deployX + Math.floor((DW - pvcTotalW) / 2);
  const pvcRowY    = hasPvcs ? podsY + maxPodH + PVC_SEP : 0;
  const svgH       = hasPvcs ? pvcRowY + PVCH + PAD : podsY + maxPodH + PAD;

  // Namespace boundary box encompasses all namespace-scoped resources
  const nsBoxLeft   = PAD / 2;
  const nsBoxRight  = saX + SAW + NS_PAD;
  const nsBoxTop    = deployY - NS_PAD - NS_LABEL_H;  // = PAD (the original top)
  const nsBoxBottom = hasPvcs ? pvcRowY + PVCH + NS_PAD : podsY + maxPodH + NS_PAD;

  const arrowId     = 'k8s-arrow';
  const arrowDashId = 'k8s-arrow-dash';

  // ── shared SVG body ────────────────────────────────────────────────────────
  // Used in both standalone and embed modes. The <defs> are only needed
  // in standalone mode (the parent SVG handles it in embed mode).
  const ox = embedOffset?.x ?? 0;
  const oy = embedOffset?.y ?? 0;

  const diagramBody = (
    <g transform={embedOffset ? `translate(${ox},${oy})` : undefined}>
      {!embedOffset && (
        <defs>
          <marker id={arrowId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={divClr} />
          </marker>
          <marker id={arrowDashId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={txtSub} opacity="0.4" />
          </marker>
        </defs>
      )}

      {/* ── Namespace boundary ────────────────────────────────────── */}
      <rect
        x={nsBoxLeft} y={nsBoxTop}
        width={nsBoxRight - nsBoxLeft} height={nsBoxBottom - nsBoxTop}
        rx={12} fill={primary + '06'} stroke={primary} strokeWidth={1.5}
          strokeDasharray="9 5" strokeOpacity={0.35}
        />
        {/* label header bar */}
        <rect x={nsBoxLeft} y={nsBoxTop} width={nsBoxRight - nsBoxLeft} height={NS_LABEL_H}
          rx={12} fill={primary + '14'} />
        <rect x={nsBoxLeft} y={nsBoxTop + NS_LABEL_H - 5} width={nsBoxRight - nsBoxLeft} height={5}
          fill={primary + '14'} />
        <text x={nsBoxLeft + 14} y={nsBoxTop + NS_LABEL_H / 2 + 1}
          dominantBaseline="middle" fontSize={10} fontWeight={700}
          fill={primary} letterSpacing="0.08em">
          {'ns: ' + deployment.namespace}
        </text>

        {/* Ingress → Service bezier lines */}
        {ingresses.map((_, ii) => {
          const ingCY = ingGroupY + ii * (SH + SV_GAP) + SH / 2;
          if (nSvcs > 0) {
            return services.map((_s, si) => {
              const svcCY = svcsGroupY + si * (SH + SV_GAP) + SH / 2;
              const mx    = ingX + SW + H_JOIN / 2;
              return (
                <path key={'il-' + ii + '-' + si}
                  d={'M ' + (ingX + SW) + ' ' + ingCY + ' C ' + mx + ' ' + ingCY + ' ' + (svcsX - H_JOIN / 2) + ' ' + svcCY + ' ' + svcsX + ' ' + svcCY}
                  fill="none" stroke={divClr} strokeWidth={1.5} strokeDasharray="4 3"
                  markerEnd={'url(#' + arrowId + ')'} />
              );
            });
          }
          const mx = ingX + SW + H_JOIN / 2;
          return (
            <path key={'il-' + ii}
              d={'M ' + (ingX + SW) + ' ' + ingCY + ' C ' + mx + ' ' + ingCY + ' ' + (deployX - H_JOIN / 2) + ' ' + deployCY + ' ' + deployX + ' ' + deployCY}
              fill="none" stroke={divClr} strokeWidth={1.5} strokeDasharray="4 3"
              markerEnd={'url(#' + arrowId + ')'} />
          );
        })}

        {/* Service → Deploy bezier lines */}
        {services.map((_, si) => {
          const svcCY = svcsGroupY + si * (SH + SV_GAP) + SH / 2;
          const mx    = svcsX + SW + H_JOIN / 2;
          return (
            <path key={'sl-' + si}
              d={'M ' + (svcsX + SW) + ' ' + svcCY + ' C ' + mx + ' ' + svcCY + ' ' + mx + ' ' + deployCY + ' ' + deployX + ' ' + deployCY}
              fill="none" stroke={divClr} strokeWidth={1.5} markerEnd={'url(#' + arrowId + ')'} />
          );
        })}

        {/* Deploy → ServiceAccount line */}
        <path
          d={'M ' + (deployX + DW) + ' ' + deployCY + ' C ' + (deployX + DW + H_JOIN / 2) + ' ' + deployCY + ' ' + (saX - H_JOIN / 2) + ' ' + (saY + SAH / 2) + ' ' + saX + ' ' + (saY + SAH / 2)}
          fill="none" stroke={divClr} strokeWidth={1.5} strokeDasharray="6 3" opacity={0.5}
          markerEnd={'url(#' + arrowDashId + ')'} />

        {/* Deploy → Pod fan-out lines */}
        {pods.map((pod, pi) => {
          const podCX = podsStartX + pi * (PW + PH_GAP) + PW / 2;
          const cpY   = deployY + DH + V_SEP / 2;
          return (
            <path key={'pl-' + pod.name}
              d={'M ' + deployCX + ' ' + (deployY + DH) + ' C ' + deployCX + ' ' + cpY + ' ' + podCX + ' ' + cpY + ' ' + podCX + ' ' + podsY}
              fill="none" stroke={divClr} strokeWidth={1.5} markerEnd={'url(#' + arrowId + ')'} />
          );
        })}

        {/* ── Deployment hub ────────────────────────────────────────── */}
        <g style={{ cursor: 'default' }}>
          {/* background card */}
          <rect x={deployX} y={deployY} width={DW} height={DH} rx={10}
            fill={bgPaper} stroke={primary} strokeWidth={2.5} />
          {/* color strip */}
          <rect x={deployX} y={deployY} width={DW} height={P_STRIP} rx={10} fill={primary} />
          <rect x={deployX} y={deployY + 4} width={DW} height={P_STRIP - 4} fill={primary} />
          {/* type icon + label */}
          <KubeIcon type="Deployment" x={deployX + 10} y={deployY + 9} size={18} clr={primary} />
          <text x={deployX + 32} y={deployY + 22} fontSize={10} fill={txtSub} letterSpacing="0.07em">
            DEPLOYMENT
          </text>
          {/* ⓘ Info button */}
          <g style={{ cursor: 'pointer' }} onClick={() => onNodeInfo({
            kind: 'Deployment', name: deployment.name,
            fields: [
              { label: 'Namespace',        value: deployment.namespace },
              { label: 'Replicas',         value: deployment.readyReplicas + ' / ' + deployment.replicas + ' ready' },
              { label: 'Strategy',         value: deployment.strategy },
              { label: 'Service Account',  value: deployment.serviceAccountName },
              { label: 'Containers',       value: deployment.specContainers.map(c => c.name).join(', ') || '—' },
              { label: 'Volumes',          value: deployment.volumes.length ? deployment.volumes.map(v => v.name).join(', ') : '—' },
            ],
          })}>
            <circle cx={deployX + DW - 14} cy={deployY + 30} r={9}
              fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
            <text x={deployX + DW - 14} y={deployY + 30} textAnchor="middle" dominantBaseline="middle"
              fontSize={10} fontWeight={700} fill={primary} fontStyle="italic">i</text>
          </g>
          {/* manifest button */}
          {onManifestClick && (
            <ManifestIcon cx={deployX + DW - 38} cy={deployY + 30} clr={clrOk}
              onClick={() => onManifestClick('Deployment', deployment.name, deployment.namespace)} />
          )}
          {/* name */}
          <text x={deployX + 12} y={deployY + 46} fontSize={16} fontWeight={700} fill={txtMain}>
            {trunc(deployment.name, 22)}
          </text>
          {/* ready counter + strategy */}
          <text x={deployX + 12} y={deployY + 66} fontSize={12}
            fill={deployment.readyReplicas >= deployment.replicas ? clrOk : clrWarning}>
            {deployment.readyReplicas}/{deployment.replicas} pods ready
          </text>
          <text x={deployX + DW - 12} y={deployY + 66} textAnchor="end"
            fontSize={11} fill={txtSub}>{deployment.strategy}</text>
          {/* pill row divider */}
          <line x1={deployX + 8} y1={deployY + DH - 34} x2={deployX + DW - 8} y2={deployY + DH - 34}
            stroke={divClr} strokeOpacity={0.4} />
          {/* ↺ Restart pill */}
          <g style={{ cursor: 'pointer' }} onClick={onRestartClick}>
            <rect x={deployX + 8} y={deployY + DH - 30} width={98} height={24} rx={12}
              fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
            <text x={deployX + 57} y={deployY + DH - 18} textAnchor="middle"
              dominantBaseline="middle" fontSize={11} fontWeight={700} fill={primary}>
              &#8635; Restart
            </text>
          </g>
          {/* ↕ Scale pill */}
          <g style={{ cursor: 'pointer' }} onClick={onScaleClick}>
            <rect x={deployX + 112} y={deployY + DH - 30} width={98} height={24} rx={12}
              fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
            <text x={deployX + 161} y={deployY + DH - 18} textAnchor="middle"
              dominantBaseline="middle" fontSize={11} fontWeight={700} fill={primary}>
              &#8597; Scale
            </text>
          </g>
          {/* ⚡ Events pill */}
          <g style={{ cursor: 'pointer' }} onClick={onEventsClick}>
            <rect x={deployX + 216} y={deployY + DH - 30} width={98} height={24} rx={12}
              fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
            <text x={deployX + 265} y={deployY + DH - 18} textAnchor="middle"
              dominantBaseline="middle" fontSize={11} fontWeight={700} fill={primary}>
              &#9889; Events
            </text>
          </g>
        </g>

        {/* ── Service nodes ──────────────────────────────────────────── */}
        {services.map((svc, si) => {
          const svcY = svcsGroupY + si * (SH + SV_GAP);
          const svcC = svcTypeColor(svc.type);
          return (
            <g key={'svc-' + svc.name}>
              <rect x={svcsX} y={svcY} width={SW} height={SH} rx={8}
                fill={bgPaper} stroke={svcC} strokeWidth={1.5} />
              <rect x={svcsX} y={svcY} width={SW} height={5} rx={8} fill={svcC} />
              <rect x={svcsX} y={svcY + 3} width={SW} height={2} fill={svcC} />
              <KubeIcon type="Service" x={svcsX + 8} y={svcY + 9} size={16} clr={svcC} />
              <text x={svcsX + 28} y={svcY + 20} fontSize={10} fill={txtSub} letterSpacing="0.06em">
                {'SERVICE \u00b7 ' + svc.type.toUpperCase()}
              </text>
              <text x={svcsX + 10} y={svcY + 40} fontSize={13} fontWeight={700} fill={txtMain}>
                {trunc(svc.name, 22)}
              </text>
              <text x={svcsX + 10} y={svcY + 58} fontSize={10.5} fill={txtSub} fontFamily="monospace">
                {svc.clusterIP ?? '\u2014'}
              </text>
              {svc.ports.length > 0 && (
                <text x={svcsX + 10} y={svcY + SH - 11} fontSize={10.5} fill={svcC} fontFamily="monospace">
                  {svc.ports.slice(0, 2).join(' \u00b7 ')}
                </text>
              )}
              {/* ⓘ Info button */}
              <g style={{ cursor: 'pointer' }} onClick={() => onNodeInfo({
                kind: 'Service', name: svc.name,
                fields: [
                  { label: 'Type',       value: svc.type },
                  { label: 'Cluster IP', value: svc.clusterIP ?? '—', mono: true },
                  { label: 'Ports',      value: svc.ports.join(', ') || '—', mono: true },
                ],
              })}>
                <circle cx={svcsX + SW - 14} cy={svcY + SH / 2} r={9}
                  fill={svcC + '22'} stroke={svcC} strokeWidth={1.2} />
                <text x={svcsX + SW - 14} y={svcY + SH / 2} textAnchor="middle" dominantBaseline="middle"
                  fontSize={10} fontWeight={700} fill={svcC} fontStyle="italic">i</text>
              </g>
              {/* manifest button */}
              {onManifestClick && (
                <ManifestIcon cx={svcsX + SW - 38} cy={svcY + SH / 2} clr={clrOk}
                  onClick={() => onManifestClick('Service', svc.name, deployment.namespace)} />
              )}
            </g>
          );
        })}
        {/* ── Ingress nodes ──────────────────────────────────────────── */}
        {ingresses.map((ing, ii) => {
          const ingY    = ingGroupY + ii * (SH + SV_GAP);
          const ingClr  = ing.tls ? clrOk : primary;
          const firstRule = ing.rules[0];
          return (
            <g key={'ing-' + ing.name}>
              <rect x={ingX} y={ingY} width={SW} height={SH} rx={8}
                fill={bgPaper} stroke={ingClr} strokeWidth={1.5} />
              <rect x={ingX} y={ingY} width={SW} height={5} rx={8} fill={ingClr} />
              <rect x={ingX} y={ingY + 3} width={SW} height={2} fill={ingClr} />
              <KubeIcon type="Ingress" x={ingX + 8} y={ingY + 9} size={16} clr={ingClr} />
              <text x={ingX + 28} y={ingY + 20} fontSize={10} fill={txtSub} letterSpacing="0.06em">
                {'INGRESS' + (ing.className ? ' \u00b7 ' + ing.className.toUpperCase() : '')}
              </text>
              {ing.tls && (
                <text x={ingX + SW - 10} y={ingY + 20} textAnchor="end"
                  fontSize={10} fontWeight={700} fill={clrOk}>TLS</text>
              )}
              <text x={ingX + 10} y={ingY + 40} fontSize={13} fontWeight={700} fill={txtMain}>
                {trunc(ing.name, 22)}
              </text>
              {firstRule && (firstRule.host || firstRule.paths[0]) && (
                <text x={ingX + 10} y={ingY + SH - 11} fontSize={10} fill={ingClr} fontFamily="monospace">
                  {trunc(firstRule.host || firstRule.paths[0] || '', 26)}
                </text>
              )}
              {/* ⓘ Info button */}
              <g style={{ cursor: 'pointer' }} onClick={() => onNodeInfo({
                kind: 'Ingress', name: ing.name,
                fields: [
                  { label: 'Class',            value: ing.className ?? '(default)' },
                  { label: 'TLS',              value: ing.tls ? 'yes' : 'no' },
                  { label: 'Backend Services', value: (ing.backendServices ?? []).join(', ') || '—', mono: true },
                  ...ing.rules.map((r, ri) => ({
                    label: 'Rule ' + (ri + 1),
                    value: (r.host || '*') + ' \u2192 ' + (r.paths.join(', ') || '/'),
                    mono: true,
                  })),
                ],
              })}>
                <circle cx={ingX + SW - 14} cy={ingY + SH / 2} r={9}
                  fill={ingClr + '22'} stroke={ingClr} strokeWidth={1.2} />
                <text x={ingX + SW - 14} y={ingY + SH / 2} textAnchor="middle" dominantBaseline="middle"
                  fontSize={10} fontWeight={700} fill={ingClr} fontStyle="italic">i</text>
              </g>
              {/* manifest button */}
              {onManifestClick && (
                <ManifestIcon cx={ingX + SW - 38} cy={ingY + SH / 2} clr={clrOk}
                  onClick={() => onManifestClick('Ingress', ing.name, deployment.namespace)} />
              )}
            </g>
          );
        })}

        {/* ── ServiceAccount node ────────────────────────────────────── */}
        <g>
          <rect x={saX} y={saY} width={SAW} height={SAH} rx={8}
            fill={bgPaper} stroke={divClr} strokeWidth={1.5} strokeDasharray="5 3" />
          <KubeIcon type="ServiceAccount" x={saX + 8} y={saY + 7} size={16} clr={txtSub} />
          <text x={saX + 28} y={saY + 18} fontSize={10} fill={txtSub} letterSpacing="0.06em">
            SERVICEACCOUNT
          </text>
          <text x={saX + 10} y={saY + 38} fontSize={12.5} fontWeight={700} fill={txtMain}>
            {trunc(deployment.serviceAccountName, 18)}
          </text>
          {/* ⓘ Info button */}
          <g style={{ cursor: 'pointer' }} onClick={() => onNodeInfo({
            kind: 'ServiceAccount', name: deployment.serviceAccountName,
            fields: [
              { label: 'Namespace', value: deployment.namespace },
              { label: 'Name',      value: deployment.serviceAccountName, mono: true },
            ],
          })}>
            <circle cx={saX + SAW - 12} cy={saY + SAH / 2} r={9}
              fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
            <text x={saX + SAW - 12} y={saY + SAH / 2} textAnchor="middle" dominantBaseline="middle"
              fontSize={10} fontWeight={700} fill={primary} fontStyle="italic">i</text>
          </g>
          {/* manifest button */}
          {onManifestClick && (
            <ManifestIcon cx={saX + SAW - 36} cy={saY + SAH / 2} clr={clrOk}
              onClick={() => onManifestClick('ServiceAccount', deployment.serviceAccountName, deployment.namespace)} />
          )}
        </g>

        {/* ── Pod nodes ──────────────────────────────────────────────── */}
        {pods.length === 0 ? (
          <g>
            <rect x={podsStartX} y={podsY} width={PW} height={P_HDR + LOGS_ROW + P_PAD} rx={8}
              fill={bgDefault} stroke={divClr} strokeWidth={1} strokeDasharray="5 3" />
            <text x={podsStartX + PW / 2} y={podsY + (P_HDR + LOGS_ROW + P_PAD) / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={12} fill={txtSub} fontStyle="italic">No pods running</text>
          </g>
        ) : pods.map((pod, pi) => {
          const px   = podsStartX + pi * (PW + PH_GAP);
          const ph   = podH(pod);
          const pC   = phaseColor(pod.phase);
          const ctrs: GraphContainer[] = pod.containers.length > 0
            ? pod.containers
            : deployment.specContainers.map(sc => ({
                name: sc.name, ready: false, restartCount: 0, image: sc.image, state: 'waiting',
              }));
          const vols   = deployment.volumes;
          const pillY  = podsY + ph - LOGS_ROW + 2;

          return (
            <g key={pod.name} style={{ cursor: 'default' }}>
              {/* invisible hit area — removed (no whole-card click) */}
              {/* card bg + phase border */}
              <rect x={px} y={podsY} width={PW} height={ph} rx={8}
                fill={bgDefault} stroke={pC} strokeWidth={2} strokeOpacity={0.8} />
              {/* phase color strip */}
              <rect x={px} y={podsY} width={PW} height={P_STRIP} rx={8} fill={pC} />
              <rect x={px} y={podsY + 4} width={PW} height={P_STRIP - 4} fill={pC} />
              {/* type icon + label */}
              <KubeIcon type="Pod" x={px + 8} y={podsY + 9} size={14} clr={pC} />
              <text x={px + 26} y={podsY + 20} fontSize={9} fill={txtSub} letterSpacing="0.06em">POD</text>
              {/* ⓘ Info button */}
              <g style={{ cursor: 'pointer' }} onClick={e => {
                e.stopPropagation();
                onNodeInfo({
                  kind: 'Pod', name: pod.name,
                  fields: [
                    { label: 'Phase',      value: pod.phase },
                    { label: 'IP',         value: pod.podIP ?? '—', mono: true },
                    { label: 'Node',       value: pod.node ?? '—', mono: true },
                    { label: 'Ready',      value: pod.ready ? 'yes' : 'no' },
                    { label: 'Restarts',   value: String(pod.restarts) },
                    { label: 'Start Time', value: pod.startTime ?? '—' },
                    ...pod.containers.map(c => ({
                      label: 'Container: ' + c.name,
                      value: c.state + (c.ready ? ' ✓' : ' ✗') + '  restarts: ' + c.restartCount,
                    })),
                  ],
                });
              }}>
                <circle cx={px + PW - 14} cy={podsY + P_STRIP + 22} r={9}
                  fill={pC + '22'} stroke={pC} strokeWidth={1.2} />
                <text x={px + PW - 14} y={podsY + P_STRIP + 22} textAnchor="middle" dominantBaseline="middle"
                  fontSize={10} fontWeight={700} fill={pC} fontStyle="italic">i</text>
              </g>

              {/* pod name */}
              <text x={px + 12} y={podsY + P_STRIP + 34} fontSize={12} fontWeight={700} fill={txtMain}>
                {trunc(pod.name, 30)}
              </text>
              {/* IP address */}
              <text x={px + 12} y={podsY + P_STRIP + 50} fontSize={10.5} fill={txtSub} fontFamily="monospace">
                {pod.podIP ?? '\u2014'}
              </text>
              {/* Node name */}
              <text x={px + 12} y={podsY + P_STRIP + 66} fontSize={10.5} fill={txtSub} fontFamily="monospace">
                {trunc((pod.node ?? 'unknown').split('.')[0], 30)}
              </text>

              {/* phase dot + text */}
              <circle cx={px + 13} cy={podsY + P_HDR - 14} r={5} fill={pC} />
              <text x={px + 25} y={podsY + P_HDR - 9} fontSize={11} fill={pC} dominantBaseline="middle">
                {pod.phase}
              </text>
              {/* ready indicator */}
              <text x={px + PW - 12} y={podsY + P_HDR - 9} textAnchor="end" dominantBaseline="middle"
                fontSize={11} fill={pod.ready ? clrOk : clrError}>
                {pod.ready ? '\u2713 ready' : '\u2717 not ready'}
              </text>
              {/* restarts badge */}
              {pod.restarts > 0 && (
                <g>
                  <rect x={px + PW - 46} y={podsY + P_HDR - 30} width={38} height={18} rx={9} fill={clrWarning + '33'} />
                  <text x={px + PW - 27} y={podsY + P_HDR - 21} textAnchor="middle"
                    dominantBaseline="middle" fontSize={10} fill={clrWarning}>{pod.restarts + '\u21ba'}</text>
                </g>
              )}

              {/* header divider */}
              <line x1={px + 8} y1={podsY + P_HDR} x2={px + PW - 8} y2={podsY + P_HDR}
                stroke={divClr} strokeOpacity={0.6} />

              {/* container rows */}
              {ctrs.map((c, ci) => {
                const cy   = podsY + P_HDR + ci * C_H;
                const cClr = ctrStateColor(c.state, c.ready);
                // Parse image into registry+repo / tag parts
                const imgFull   = c.image ?? '';
                const colonIdx  = imgFull.lastIndexOf(':');
                const imgNoTag  = colonIdx > 0 ? imgFull.slice(0, colonIdx) : imgFull;
                const imgTag    = colonIdx > 0 ? imgFull.slice(colonIdx + 1) : 'latest';
                const slashIdx  = imgNoTag.lastIndexOf('/');
                const imgRegistry = slashIdx > 0 ? imgNoTag.slice(0, slashIdx) : '';
                const imgRepo     = slashIdx > 0 ? imgNoTag.slice(slashIdx + 1) : imgNoTag;
                return (
                  <g key={pod.name + '-c' + ci}>
                    <circle cx={px + 14} cy={cy + C_H / 2} r={4.5} fill={cClr} />
                    <text x={px + 26} y={cy + C_H / 2 - 7} fontSize={11} fontWeight={600} fill={txtMain}>
                      {trunc(c.name, 20)}
                    </text>
                    <text x={px + 26} y={cy + C_H / 2 + 8} fontSize={10} fill={txtSub} fontFamily="monospace">
                      {shortImage(c.image)}
                    </text>
                    {/* container ⓘ */}
                    <g style={{ cursor: 'pointer' }} onClick={e => {
                      e.stopPropagation();
                      onNodeInfo({
                        kind: 'Container', name: c.name,
                        fields: [
                          { label: 'State',     value: c.state + (c.ready ? ' \u2713' : ' \u2717') },
                          { label: 'Restarts',  value: String(c.restartCount) },
                          { label: 'Registry',  value: imgRegistry || '(default)', mono: true },
                          { label: 'Image',     value: imgRepo, mono: true },
                          { label: 'Tag / SHA', value: imgTag, mono: true },
                          { label: 'Full ref',  value: imgFull, mono: true },
                        ],
                      });
                    }}>
                      <circle cx={px + PW - 14} cy={cy + C_H / 2} r={8}
                        fill={cClr + '22'} stroke={cClr} strokeWidth={1.1} />
                      <text x={px + PW - 14} y={cy + C_H / 2} textAnchor="middle" dominantBaseline="middle"
                        fontSize={9} fontWeight={700} fill={cClr} fontStyle="italic">i</text>
                    </g>
                    {ci < ctrs.length - 1 && (
                      <line x1={px + 10} y1={cy + C_H} x2={px + PW - 10} y2={cy + C_H}
                        stroke={divClr} strokeOpacity={0.2} />
                    )}
                  </g>
                );
              })}

              {/* volumes strip */}
              {hasVols && vols.length > 0 && (() => {
                const vY = podsY + P_HDR + ctrs.length * C_H;
                return (
                  <g>
                    <line x1={px + 8} y1={vY} x2={px + PW - 8} y2={vY}
                      stroke={divClr} strokeOpacity={0.35} />
                    {vols.slice(0, 3).map((v, vi) => {
                      const vx = px + 10 + vi * (VOL_CW + 5);
                      const vs = volStyle(v);
                      return (
                        <g key={pod.name + '-v' + vi}>
                          <rect x={vx} y={vY + 4} width={VOL_CW} height={VOL_CH} rx={9} fill={vs.bg} />
                          <text x={vx + 7} y={vY + 4 + VOL_CH / 2} fontSize={9} fill={vs.fg}
                            dominantBaseline="middle">{vs.abbr + ' ' + vs.label}</text>
                        </g>
                      );
                    })}
                    {vols.length > 3 && (
                      <text x={px + PW - 10} y={vY + 4 + VOL_CH / 2} textAnchor="end"
                        fontSize={9} fill={txtSub} dominantBaseline="middle">+{vols.length - 3}</text>
                    )}
                  </g>
                );
              })()}

              {/* Open Logs button — sole click target to open logs */}
              <line x1={px + 8} y1={pillY - 4} x2={px + PW - 8} y2={pillY - 4}
                stroke={divClr} strokeOpacity={0.35} />
              <g style={{ cursor: 'pointer' }} onClick={() => onPodClick(pod)}>
                <rect x={px + 10} y={pillY} width={PW - 20} height={22} rx={11}
                  fill={primary + '22'} stroke={primary} strokeWidth={1.2} />
                <text x={px + PW / 2} y={pillY + 11} textAnchor="middle" dominantBaseline="middle"
                  fontSize={11} fontWeight={700} fill={primary}>{'\u2261 Open Logs'}</text>
              </g>
            </g>
          );
        })}

        {/* ── PVC nodes \u2014 namespace-scoped persistent volume claims \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
        {hasPvcs && pvcs.map((pvc, pi) => {
          const px    = pvcStartX + pi * (PVCW + PVCGAP);
          const py    = pvcRowY;
          const pvCX  = px + PVCW / 2;
          const bound = pvc.status === 'Bound';
          const pvcClr = bound ? clrStorage : clrWarning;
          const cpY   = py - 14;
          return (
            <g key={'pvc-' + pvc.name}>
              {/* Deploy \u2192 PVC curved dashed line */}
              <path
                d={`M ${deployCX} ${deployY + DH} C ${deployCX} ${cpY} ${pvCX} ${cpY} ${pvCX} ${py}`}
                fill="none" stroke={pvcClr} strokeWidth={1.5} strokeDasharray="5 3" opacity={0.65}
                markerEnd={`url(#${arrowDashId})`} />
              {/* Card */}
              <rect x={px} y={py} width={PVCW} height={PVCH} rx={7}
                fill={bgPaper} stroke={pvcClr} strokeWidth={1.5} />
              <rect x={px} y={py} width={PVCW} height={4} rx={7} fill={pvcClr + '88'} />
              <rect x={px} y={py + 2} width={PVCW} height={2} fill={pvcClr + '88'} />
              <KubeIcon type="PersistentVolume" x={px + 8} y={py + 8} size={13} clr={pvcClr} />
              <text x={px + 25} y={py + 16} fontSize={8} fontFamily="monospace" fontWeight={600}
                fill={txtSub} letterSpacing={0.4} style={{ dominantBaseline: 'middle', userSelect: 'none' }}>
                PVC
              </text>
              <text x={px + 8} y={py + 33} fontSize={11} fontFamily="monospace" fontWeight={600}
                fill={txtMain} style={{ userSelect: 'none' }}>
                {trunc(pvc.name, 16)}
              </text>
              <text x={px + 8} y={py + 46} fontSize={8} fontFamily="monospace" fill={pvcClr}
                style={{ userSelect: 'none' }}>
                {pvc.capacity + ' \u00b7 ' + pvc.status}
              </text>
            </g>
          );
        })}
    </g>
  );

  if (embedOffset) return diagramBody;

  return (
    <Box style={{ overflowX: 'auto', padding: '16px 0', display: 'flex', justifyContent: 'center' }}>
      <svg width={svgW} height={svgH} style={{ display: 'block', fontFamily: 'inherit' }}>
        <defs>
          <marker id={arrowId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={divClr} />
          </marker>
          <marker id={arrowDashId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={txtSub} opacity="0.4" />
          </marker>
        </defs>
        {diagramBody}
      </svg>
    </Box>
  );
}

// ── Cluster bar dimension calculator (mirrors K8sClusterBar constants) ────────
function calcClusterBarDims(nodes: GraphNode[], storageClasses: GraphStorageClass[], persistentVolumes: GraphPV[]) {
  const CW = 160; const CGAP = 10; const OPAD = 14;
  const CH = 100; const CY = 12;
  const rowH  = CH + CY * 2; // 124
  const ROW_LW = 48;
  let nx = OPAD;
  for (let i = 0; i < nodes.length; i++) nx += CW + CGAP;
  const nodeRowW = Math.max(nx + OPAD - CGAP, 240);
  let sx = OPAD;
  const nSCs = storageClasses.length;
  const nPVsSliced = Math.min(persistentVolumes.length, 8);
  for (let i = 0; i < nSCs; i++) sx += CW + CGAP;
  const scPvDivW = (nSCs > 0 && nPVsSliced > 0) ? 20 : 0;
  if (scPvDivW) sx += scPvDivW;
  for (let i = 0; i < nPVsSliced; i++) sx += CW + CGAP;
  const storeRowW = Math.max(sx + OPAD - CGAP, 240);
  const hasNodes   = nodes.length > 0;
  const hasStorage = storageClasses.length > 0 || persistentVolumes.length > 0;
  const embedBarH  = (hasNodes ? rowH : 0) + (hasNodes && hasStorage ? 1 : 0) + (hasStorage ? rowH : 0);
  const embedBarW  = ROW_LW + Math.max(nodeRowW, storeRowW);
  // PV card center X values relative to embed group origin (x=0 of the group)
  const pvXPositions = persistentVolumes.slice(0, 8).map((_, k) =>
    ROW_LW + OPAD + nSCs * (CW + CGAP) + scPvDivW + k * (CW + CGAP) + CW / 2,
  );
  const storeRowY = hasNodes ? rowH + 1 : 0;
  const pvYBottom = storeRowY + CY + CH; // Y of PV card bottom within embed group
  return { embedBarW, embedBarH, hasNodes, hasStorage, pvXPositions, pvYBottom, rowH };
}

// ── Multi-namespace unified SVG canvas ───────────────────────────────────────────
function K8sMultiNsGraph({
  multiGraphData, humanitecEnvs, deploymentName, loading, error,
  onPodClick, onRestartClick, onEventsClick, onScaleClick, onNodeInfo, onManifestClick,
}: {
  multiGraphData: MultiNamespaceGraphData | null;
  humanitecEnvs: HumanitecEnvSummary[];
  deploymentName: string;
  loading: boolean;
  error: string | null;
  onPodClick: (pod: GraphPod, ns: string) => void;
  onRestartClick: (ns: string) => void;
  onEventsClick: (ns: string) => void;
  onScaleClick: (ns: string) => void;
  onNodeInfo: (info: NodeInfo) => void;
  onManifestClick?: (kind: string, name: string, namespace: string) => void;
}) {
  const theme   = useTheme();
  const primary = theme.palette.primary.main;
  const divClr  = theme.palette.divider;
  const txtMain = theme.palette.text.primary;
  const txtSub  = theme.palette.text.secondary;
  const bgPaper = theme.palette.background.paper;
  const clrOk      = theme.palette.success?.main ?? '#4caf50';
  const clrWarning = theme.palette.warning?.main ?? '#ff9800';
  const clrMuted   = theme.palette.grey?.[500] ?? '#9e9e9e';
  const clrStorage = theme.palette.info?.light ?? '#00bcd4';

  // Pan / zoom state
  const [vp, setVp]               = useState({ x: 20, y: 20, s: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef               = useRef(false);
  const lastPosRef                  = useRef({ x: 0, y: 0 });
  const svgRef                      = useRef<SVGSVGElement>(null);
  const didFitRef                   = useRef(false);
  const fitVpRef                    = useRef({ x: 20, y: 20, s: 1 });

  // Passive-safe wheel zoom
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      setVp(v => ({ ...v, s: Math.min(4, Math.max(0.12, v.s * factor)) }));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
    setVp(v => ({ ...v, x: v.x + dx, y: v.y + dy }));
  };
  const handleMouseUp = () => { isDraggingRef.current = false; setIsDragging(false); };

  // Layout constants
  const CB_HDR_H     = 28;
  const OUTER_PAD    = 20;
  const ENV_GAP      = 16;
  const ENV_HDR_H    = 38;
  const ENV_BORDER_R = 10;
  const PVCW = 140; const PVCGAP = 12;

  const namespaces = multiGraphData?.namespaces ?? [];
  const cluster    = multiGraphData?.cluster ?? { nodes: [], storageClasses: [], persistentVolumes: [] };

  const clusterBarDims  = calcClusterBarDims(cluster.nodes, cluster.storageClasses, cluster.persistentVolumes);
  const clusterSectionH = OUTER_PAD + CB_HDR_H + clusterBarDims.embedBarH + OUTER_PAD;

  const nsDims = namespaces.map(ns => calcNsDiagramDims(ns.graph, ns.ingresses, ns.pvcs ?? []));
  const maxNsW = nsDims.length > 0 ? Math.max(...nsDims.map(d => d.svgW)) : 640;

  // ── Side-by-side row packing: 2 envs per row ──────────────────────────────
  const COL_GAP = 20;
  // Canvas must fit 2 columns of maxNsW plus padding
  const totalW = Math.max(
    2 * maxNsW + COL_GAP + 2 * OUTER_PAD,
    clusterBarDims.embedBarW + 2 * OUTER_PAD,
    700,
  );
  const twoColW = totalW - 2 * OUTER_PAD;                  // inner content width
  const colW    = Math.floor((twoColW - COL_GAP) / 2);     // single column slot

  // Pack namespaces 2-per-row; last odd env gets the full inner width
  type LayoutRow = { indices: number[] };
  const envRows: LayoutRow[] = [];
  {
    let ri = 0;
    while (ri < namespaces.length) {
      if (ri + 1 < namespaces.length) { envRows.push({ indices: [ri, ri + 1] }); ri += 2; }
      else                             { envRows.push({ indices: [ri] });         ri++;     }
    }
  }

  const envXByNs = new Array<number>(namespaces.length);
  const envYByNs = new Array<number>(namespaces.length);
  const envWByNs = new Array<number>(namespaces.length);
  let curY = clusterSectionH + ENV_GAP;
  for (const row of envRows) {
    const rowH = Math.max(...row.indices.map(i => ENV_HDR_H + nsDims[i].svgH));
    if (row.indices.length === 2) {
      const [a, b] = row.indices;
      envXByNs[a] = OUTER_PAD;               envWByNs[a] = colW;      envYByNs[a] = curY;
      envXByNs[b] = OUTER_PAD + colW + COL_GAP; envWByNs[b] = colW;   envYByNs[b] = curY;
    } else {
      const [a] = row.indices;
      envXByNs[a] = OUTER_PAD; envWByNs[a] = twoColW; envYByNs[a] = curY;
    }
    curY += rowH + ENV_GAP;
  }
  const totalH    = curY + OUTER_PAD;
  const containerH = Math.min(Math.max(totalH + 80, 560), 880);

  // PV→PVC cross-section connecting lines
  const pvLines: React.ReactNode[] = [];
  if (!loading && multiGraphData) {
    const pvBottomSvgY = OUTER_PAD + CB_HDR_H + clusterBarDims.pvYBottom;
    cluster.persistentVolumes.slice(0, 8).forEach((pv, pvIdx) => {
      if (!pv.claimRef) return;
      const parts = pv.claimRef.split('/');
      if (parts.length !== 2) return;
      const [pvNs, pvPvcName] = parts;
      const nsIdx = namespaces.findIndex(ns => ns.name === pvNs);
      if (nsIdx < 0) return;
      const nsDim  = nsDims[nsIdx];
      if (nsDim.pvcRowY < 0) return;
      const nsPvcs = namespaces[nsIdx].pvcs ?? [];
      const pvcIdx = nsPvcs.findIndex(pvc => pvc.name === pvPvcName);
      if (pvcIdx < 0) return;
      const pvSvgX  = OUTER_PAD + clusterBarDims.pvXPositions[pvIdx];
      const pvcSvgX = envXByNs[nsIdx] + nsDim.pvcStartX + pvcIdx * (PVCW + PVCGAP) + PVCW / 2;
      const pvcTopY = envYByNs[nsIdx] + ENV_HDR_H + nsDim.pvcRowY;
      const midY    = (pvBottomSvgY + pvcTopY) / 2;
      pvLines.push(
        <path key={`pvlink-${pvIdx}-${nsIdx}-${pvcIdx}`}
          d={`M ${pvSvgX} ${pvBottomSvgY} C ${pvSvgX} ${midY} ${pvcSvgX} ${midY} ${pvcSvgX} ${pvcTopY}`}
          fill="none" stroke={clrStorage + '88'} strokeWidth={2} strokeDasharray="6 3"
          markerEnd="url(#mng-arrow-dash)" />,
      );
    });
  }

  // SVG export
  const handleExport = () => {
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(totalW));
    clone.setAttribute('height', String(totalH));
    clone.setAttribute('viewBox', `0 0 ${totalW} ${totalH}`);
    clone.style.cssText = '';
    const zoomGroup = clone.getElementById('mng-zoom-group');
    if (zoomGroup) zoomGroup.removeAttribute('transform');
    const svgStr = '<?xml version="1.0" encoding="utf-8"?>\n' + new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `k8s-${deploymentName}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const arrowId     = 'mng-arrow';
  const arrowDashId = 'mng-arrow-dash';

  // Auto-fit to show all content when data first arrives
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!multiGraphData || didFitRef.current || totalH <= 0) return;
    didFitRef.current = true;
    const estW = svgRef.current?.clientWidth ?? window.innerWidth - 100;
    const scaleH = (containerH - 60) / totalH;
    const scaleW = (estW - 60) / totalW;
    const fitS   = Math.min(1.0, scaleH, scaleW) * 0.96;
    const clampedS = Math.max(0.18, fitS);
    const fp = { x: 20, y: 20, s: clampedS };
    fitVpRef.current = fp;
    setVp(fp);
  }, [multiGraphData]); // only when data changes, not on every layout recalc

  return (
    <Box style={{ position: 'relative', width: '100%', height: containerH, overflow: 'hidden',
      cursor: isDragging ? 'grabbing' : 'grab' }}>
      {/* Toolbar */}
      <Box style={{
        position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex',
        gap: 4, alignItems: 'center', backgroundColor: bgPaper + 'ee',
        borderRadius: 8, padding: '4px 8px', border: '1px solid ' + divClr,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}>
        <IconButton title="Zoom in" onClick={() => setVp(v => ({ ...v, s: Math.min(4, v.s * 1.25) }))} style={{ padding: 6 }}>
          <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 700, display: 'block' }}>+</span>
        </IconButton>
        <IconButton title="Zoom out" onClick={() => setVp(v => ({ ...v, s: Math.max(0.12, v.s / 1.25) }))} style={{ padding: 6 }}>
          <span style={{ fontSize: 20, lineHeight: 1, fontWeight: 300, display: 'block' }}>{'−'}</span>
        </IconButton>
        <IconButton title="Fit to screen" onClick={() => setVp(fitVpRef.current)} style={{ padding: 6 }}>
          <span style={{ fontSize: 16, lineHeight: 1, display: 'block' }}>{'↺'}</span>
        </IconButton>
        <Box style={{ width: 1, height: 20, backgroundColor: divClr, opacity: 0.4, margin: '0 2px' }} />
        <IconButton title="Export as SVG" onClick={handleExport} style={{ padding: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', display: 'block' }}>SVG</span>
        </IconButton>
        {loading && <CircularProgress size={16} style={{ marginLeft: 2 }} />}
      </Box>

      <svg ref={svgRef} width="100%" height="100%"
        style={{ display: 'block', fontFamily: 'inherit', userSelect: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}>
        <defs>
          <marker id={arrowId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={divClr} />
          </marker>
          <marker id={arrowDashId} markerWidth="8" markerHeight="7" refX="8" refY="3.5" orient="auto">
            <path d="M0,0 L8,3.5 L0,7 Z" fill={clrStorage} opacity="0.6" />
          </marker>
        </defs>

        <g id="mng-zoom-group" transform={`translate(${vp.x},${vp.y}) scale(${vp.s})`}>

          {/* \u2500\u2500 Cluster section \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
          <rect x={1} y={1} width={totalW - 2} height={clusterSectionH - 2}
            rx={14} fill={bgPaper} stroke={divClr} strokeWidth={2} />
          <text x={OUTER_PAD + 10} y={OUTER_PAD + CB_HDR_H / 2}
            dominantBaseline="middle" fontSize={9.5} fontFamily="monospace" fontWeight={700}
            fill={txtSub} opacity={0.4} letterSpacing={2} style={{ userSelect: 'none' }}>
            CLUSTER
          </text>
          <line x1={OUTER_PAD} y1={OUTER_PAD + CB_HDR_H}
            x2={totalW - OUTER_PAD} y2={OUTER_PAD + CB_HDR_H}
            stroke={divClr} strokeOpacity={0.25} />
          {loading && (
            <circle cx={totalW - OUTER_PAD - 12} cy={OUTER_PAD + CB_HDR_H / 2} r={6}
              fill="none" stroke={primary} strokeWidth={1.5} strokeDasharray="10 5">
              <animateTransform attributeName="transform" type="rotate"
                from={`0 ${totalW - OUTER_PAD - 12} ${OUTER_PAD + CB_HDR_H / 2}`}
                to={`360 ${totalW - OUTER_PAD - 12} ${OUTER_PAD + CB_HDR_H / 2}`}
                dur="1s" repeatCount="indefinite" />
            </circle>
          )}
          {!loading && !clusterBarDims.hasNodes && !clusterBarDims.hasStorage && (
            <text x={totalW / 2} y={clusterSectionH / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={11} fill={txtSub} fontStyle="italic" opacity={0.45}
              style={{ userSelect: 'none' }}>
              cluster-scoped resources unavailable \u2014 add ClusterRole to backstage-sa
            </text>
          )}
          {/* Cluster bar: pure SVG embed (no foreignObject) */}
          {(clusterBarDims.hasNodes || clusterBarDims.hasStorage) && (
            <K8sClusterBar
              nodes={cluster.nodes}
              storageClasses={cluster.storageClasses}
              persistentVolumes={cluster.persistentVolumes}
              loading={loading}
              embedOffset={{ x: OUTER_PAD, y: OUTER_PAD + CB_HDR_H }}
            />
          )}

          {/* PV\u2192PVC connecting lines (behind env sections) */}
          {pvLines}

          {/* \u2500\u2500 Namespace env sections \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
          {error && (
            <text x={totalW / 2} y={clusterSectionH + ENV_GAP + 20}
              textAnchor="middle" fontSize={12} fill={theme.palette.error.main}>
              Failed to load: {error}
            </text>
          )}
          {!loading && namespaces.map((nsData, i) => {
            const envX  = envXByNs[i];
            const envY  = envYByNs[i];
            const envW  = envWByNs[i];
            const nsH   = nsDims[i].svgH;
            const envH  = ENV_HDR_H + nsH;
            const humEnv = humanitecEnvs.find(
              e => e.id === nsData.name || e.id.toLowerCase() === nsData.name.toLowerCase(),
            );
            const envClr = humEnv
              ? (humEnv.status === 'active' ? clrOk : humEnv.status === 'pending' ? clrWarning : clrMuted)
              : undefined;
            const envBorderClr = envClr ?? primary;

            return (
              <g key={nsData.name}>
                {/* Env boundary */}
                <rect x={envX} y={envY} width={envW} height={envH}
                  rx={ENV_BORDER_R} fill={envBorderClr + '06'}
                  stroke={envBorderClr} strokeWidth={1.5} strokeDasharray="9 5" strokeOpacity={0.5} />
                {/* Env header bg */}
                <rect x={envX} y={envY} width={envW} height={ENV_HDR_H}
                  rx={ENV_BORDER_R} fill={envBorderClr + '14'} />
                <rect x={envX} y={envY + ENV_HDR_H - 5} width={envW} height={5}
                  fill={envBorderClr + '14'} />
                {/* Env header text */}
                <text x={envX + 14} y={envY + ENV_HDR_H / 2}
                  dominantBaseline="middle" fontSize={10.5}
                  fontFamily="monospace" fill={txtSub} opacity={0.55} fontWeight={700}
                  style={{ userSelect: 'none' }}>
                  environment:
                </text>
                <text x={envX + 114} y={envY + ENV_HDR_H / 2}
                  dominantBaseline="middle" fontSize={12}
                  fontFamily="monospace" fill={txtMain} fontWeight={700}
                  style={{ userSelect: 'none' }}>
                  {nsData.name}
                </text>
                {/* Humanitec badge — right-aligned, clipped within env header */}
                {humEnv && (
                  <g>
                    <circle cx={envX + envW - 14} cy={envY + ENV_HDR_H / 2} r={4} fill={envClr} />
                    <text x={envX + envW - 22} y={envY + ENV_HDR_H / 2}
                      textAnchor="end" dominantBaseline="middle"
                      fontSize={10} fontWeight={600}
                      fill={envClr ?? primary} style={{ userSelect: 'none' }}>
                      {'Humanitec \u00b7 ' + (humEnv.displayName ?? nsData.name)}
                    </text>
                  </g>
                )}
                {/* No deployment fallback */}
                {!nsData.graph && (
                  <text x={envX + envW / 2} y={envY + ENV_HDR_H + nsH / 2}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={12} fill={txtSub} fontStyle="italic"
                    style={{ userSelect: 'none' }}>
                    {'No deployment ' + deploymentName + ' found in this namespace'}
                  </text>
                )}
                {/* Topology diagram */}
                {nsData.graph && (
                  <K8sGraphDiagram
                    graphData={nsData.graph}
                    ingresses={nsData.ingresses}
                    pvcs={nsData.pvcs ?? []}
                    embedOffset={{ x: envX, y: envY + ENV_HDR_H }}
                    onPodClick={pod => onPodClick(pod, nsData.name)}
                    onRestartClick={() => onRestartClick(nsData.name)}
                    onEventsClick={() => onEventsClick(nsData.name)}
                    onScaleClick={() => onScaleClick(nsData.name)}
                    onNodeInfo={onNodeInfo}
                    onManifestClick={onManifestClick}
                  />
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function K8sOperationsContent() {
  const { entity }             = useEntity();
  const discovery              = useApi(discoveryApiRef);
  const config                 = useApi(configApiRef);
  const { fetch: authedFetch } = useApi(fetchApiRef);
  const classes                = useStyles();
  const theme                  = useTheme();
  const clrOk      = theme.palette.success?.main ?? '#4caf50';
  const clrError   = theme.palette.error.main;

  const annotations        = entity.metadata.annotations ?? {};
  const entityName         = entity.metadata.name;
  const k8sNamespace       = annotations[K8S_NAMESPACE_ANNOTATION] || 'default';
  const deploymentName     = entityName;
  // Fall back to humanitec.com/* annotations for entities created before the skeleton was updated
  const humanitecOrgId     = annotations[HUMANITEC_ORG_ANNOTATION]     ?? annotations['humanitec.com/orgId']     ?? '';
  const humanitecProjectId = annotations[HUMANITEC_PROJECT_ANNOTATION] ?? annotations['humanitec.com/projectId'] ?? '';
  const hasHumanitec       = Boolean(humanitecOrgId);
  const isMultiNs          = Boolean(annotations[K8S_NAMESPACES_ANNOTATION]) || hasHumanitec;
  const nsList             = isMultiNs
    ? (annotations[K8S_NAMESPACES_ANNOTATION] ?? '').split(',').map((s: string) => s.trim()).filter(Boolean)
    : [k8sNamespace];

  const [graphData,    setGraphData]    = useState<GraphData | null>(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [graphError,   setGraphError]   = useState<string | null>(null);
  const [activeNs,     setActiveNs]     = useState<string>(k8sNamespace);

  const [logsOpen,     setLogsOpen]     = useState(false);
  const [selectedPod,  setSelectedPod]  = useState('');
  const [selectedPodNs, setSelectedPodNs] = useState('');
  const [logsData,     setLogsData]     = useState<string | null>(null);
  const [logsFetching, setLogsFetching] = useState(false);
  const [logsError,    setLogsError]    = useState<string | null>(null);

  const [eventsOpen,     setEventsOpen]     = useState(false);
  const [eventsData,     setEventsData]     = useState<EventSummary[] | null>(null);
  const [eventsFetching, setEventsFetching] = useState(false);
  const [eventsError,    setEventsError]    = useState<string | null>(null);

  const [restartOpen,    setRestartOpen]    = useState(false);
  const [restartLoading, setRestartLoading] = useState(false);
  const [restartResult,  setRestartResult]  = useState<ActionResult | null>(null);

  const [scaleOpen,    setScaleOpen]    = useState(false);
  const [scaleLoading, setScaleLoading] = useState(false);
  const [scaleValue,   setScaleValue]   = useState<number>(2);
  const [scaleResult,  setScaleResult]  = useState<ActionResult | null>(null);

  const [nodeInfoOpen, setNodeInfoOpen] = useState(false);
  const [nodeInfoData, setNodeInfoData] = useState<NodeInfo | null>(null);

  const [manifestOpen,    setManifestOpen]    = useState(false);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestTitle,   setManifestTitle]   = useState('');
  const [manifestData,    setManifestData]    = useState<string | null>(null);
  const [manifestError,   setManifestError]   = useState<string | null>(null);

  // ── Multi-namespace state ───────────────────────────────────────────────
  const [multiGraphData,    setMultiGraphData]    = useState<MultiNamespaceGraphData | null>(null);
  const [multiGraphLoading, setMultiGraphLoading] = useState(false);
  const [multiGraphError,   setMultiGraphError]   = useState<string | null>(null);
  const [humanitecEnvs,     setHumanitecEnvs]     = useState<HumanitecEnvSummary[]>([]);

  const getBaseUrl = useCallback(() => discovery.getBaseUrl('app'), [discovery]);

  const fetchGraph = useCallback(async () => {
    setGraphLoading(true);
    setGraphError(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        baseUrl + '/api/day2-actions/deployment-graph?namespace=' + encodeURIComponent(k8sNamespace) + '&deploymentName=' + encodeURIComponent(deploymentName),
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'HTTP ' + resp.status);
      setGraphData(data as GraphData);
    } catch (err: any) {
      setGraphError(err.message || 'Failed to load deployment graph');
    } finally {
      setGraphLoading(false);
    }
  }, [authedFetch, getBaseUrl, k8sNamespace, deploymentName]);

  useEffect(() => {
    if (annotations[K8S_NAMESPACES_ANNOTATION]) {
      // Annotation present: namespaces are known immediately
      fetchMultiGraph();
      fetchHumanitecEnvSummaries();
    } else if (hasHumanitec) {
      // No annotation yet (old entity or entity-provider entity): derive namespaces from Humanitec
      fetchHumanitecEnvSummaries().then(envIds => {
        if (envIds.length > 0) {
          fetchMultiGraph(envIds.join(','));
        } else {
          fetchGraph();
        }
      });
    } else {
      fetchGraph();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = useCallback(async (podName: string, ns?: string) => {
    if (!podName) return;
    const logNs = ns ?? k8sNamespace;
    setLogsFetching(true);
    setLogsData(null);
    setLogsError(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        baseUrl + '/api/day2-actions/logs?namespace=' + encodeURIComponent(logNs) + '&podName=' + encodeURIComponent(podName) + '&tailLines=200',
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'HTTP ' + resp.status);
      setLogsData(data.logs ?? '');
    } catch (err: any) {
      setLogsError(err.message || 'Failed to fetch logs');
    } finally {
      setLogsFetching(false);
    }
  }, [authedFetch, getBaseUrl, k8sNamespace]);

  const handlePodClick = (pod: GraphPod, ns?: string) => {
    const resolvedNs = ns ?? k8sNamespace;
    setSelectedPod(pod.name);
    setSelectedPodNs(resolvedNs);
    setLogsData(null);
    setLogsOpen(true);
    fetchLogs(pod.name, resolvedNs);
  };

  const fetchEvents = useCallback(async (ns?: string) => {
    const evtNs = ns ?? k8sNamespace;
    setEventsFetching(true);
    setEventsError(null);
    setEventsData(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        baseUrl + '/api/day2-actions/events?namespace=' + encodeURIComponent(evtNs) + '&deploymentName=' + encodeURIComponent(deploymentName),
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'HTTP ' + resp.status);
      setEventsData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setEventsError(err.message || 'Failed to fetch events');
    } finally {
      setEventsFetching(false);
    }
  }, [authedFetch, getBaseUrl, k8sNamespace, deploymentName]);

  const handleRestart = async () => {
    setRestartLoading(true);
    setRestartResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(baseUrl + '/api/day2-actions/restart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: activeNs, deploymentName }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRestartResult({ ok: false, message: data.error || 'HTTP ' + resp.status });
      } else {
        const podList: string[] = data.pods ?? [];
        setRestartResult({
          ok: true, message: data.message,
          detail: podList.length ? 'Pods: ' + podList.join(', ') : undefined,
        });
        setTimeout(() => fetchGraph(), 4000);
      }
    } catch (err: any) {
      setRestartResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setRestartLoading(false);
    }
  };

  const handleScale = async () => {
    setScaleLoading(true);
    setScaleResult(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(baseUrl + '/api/day2-actions/scale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ namespace: activeNs, deploymentName, replicas: scaleValue }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setScaleResult({ ok: false, message: data.error || 'HTTP ' + resp.status });
      } else {
        setScaleResult({ ok: true, message: data.message });
        setTimeout(() => fetchGraph(), 4000);
      }
    } catch (err: any) {
      setScaleResult({ ok: false, message: err.message || 'Request failed' });
    } finally {
      setScaleLoading(false);
    }
  };

  const fetchManifest = useCallback(async (kind: string, name: string, namespace: string) => {
    setManifestTitle(kind + ' \u2014 ' + name);
    setManifestOpen(true);
    setManifestLoading(true);
    setManifestError(null);
    setManifestData(null);
    try {
      const baseUrl = await getBaseUrl();
      const resp = await authedFetch(
        baseUrl + '/api/day2-actions/manifest?namespace=' + encodeURIComponent(namespace) +
        '&kind=' + encodeURIComponent(kind) + '&name=' + encodeURIComponent(name),
      );
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error || 'HTTP ' + resp.status);
      setManifestData(JSON.stringify(body.manifest, null, 2));
    } catch (err: any) {
      setManifestError(err.message || 'Failed to fetch manifest');
    } finally {
      setManifestLoading(false);
    }
  }, [authedFetch, getBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMultiGraph = useCallback(async (namespacesOverride?: string) => {
    setMultiGraphError(null);
    try {
      const baseUrl = await getBaseUrl();
      const namespacesParam = namespacesOverride ?? annotations[K8S_NAMESPACES_ANNOTATION] ?? k8sNamespace;
      const resp = await authedFetch(
        baseUrl + '/api/day2-actions/multi-namespace-graph?namespaces=' +
        encodeURIComponent(namespacesParam) + '&deploymentName=' + encodeURIComponent(deploymentName),
      );
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'HTTP ' + resp.status);
      setMultiGraphData(data as MultiNamespaceGraphData);
    } catch (err: any) {
      setMultiGraphError(err.message || 'Failed to load multi-namespace graph');
    } finally {
      setMultiGraphLoading(false);
    }
  }, [authedFetch, getBaseUrl, annotations, k8sNamespace, deploymentName]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHumanitecEnvSummaries = useCallback(async (): Promise<string[]> => {
    if (!humanitecOrgId || !humanitecProjectId) return [];
    try {
      const backendUrl = config.getString('backend.baseUrl');
      const proxyBase  = `${backendUrl}/api/proxy/humanitec-v2`;
      const envsRes = await fetch(`${proxyBase}/orgs/${humanitecOrgId}/projects/${humanitecProjectId}/envs`);
      if (!envsRes.ok) return [];
      const envsList: any[] = (await envsRes.json()).items ?? [];
      const summaries: HumanitecEnvSummary[] = await Promise.all(
        envsList.map(async env => {
          let resourceTypes: string[] = [];
          try {
            const resRes = await fetch(
              `${proxyBase}/orgs/${humanitecOrgId}/active-resources?env_id=${env.id}&project_id=${humanitecProjectId}`,
            );
            if (resRes.ok) {
              const items: any[] = (await resRes.json()).items ?? [];
              resourceTypes = [...new Set<string>(items.map((r: any) => r.type).filter(Boolean))];
            }
          } catch { /* ignore */ }
          return {
            id:            env.id,
            displayName:   env.display_name ?? env.id,
            status:        env.status ?? 'active',
            resourceTypes,
          };
        }),
      );
      setHumanitecEnvs(summaries);
      return summaries.map(s => s.id);
    } catch { return []; }
  }, [config, humanitecOrgId, humanitecProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const graphPods = isMultiNs
    ? (multiGraphData?.namespaces.find(n => n.name === activeNs)?.graph?.pods ?? [])
    : (graphData?.pods ?? []);

  // Shared drawer paper style
  const drawerPaper = { borderRadius: '20px 20px 0 0', overflow: 'hidden' };
  const handle = (
    <Box style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
      <Box style={{ width: 44, height: 4, borderRadius: 2, backgroundColor: theme.palette.divider }} />
    </Box>
  );

  return (
    <>
      <InfoCard
        title="Kubernetes Operations"
        subheader={
          <span>
            Deployment: <strong>{deploymentName}</strong>
            {' \u00b7 '}
            {isMultiNs
              ? <span>Namespaces: <strong>{nsList.join(', ')}</strong></span>
              : <span>Namespace: <strong>{k8sNamespace}</strong></span>
            }
          </span>
        }
        action={
          <IconButton
            onClick={() => isMultiNs ? fetchMultiGraph() : fetchGraph()}
            disabled={isMultiNs ? multiGraphLoading : graphLoading}
            aria-label="refresh"
          >
            <RefreshIcon />
          </IconButton>
        }
      >
        {/* ── Map ──────────────────────────────────────────────────── */}
        {isMultiNs ? (
          /* ── Multi-namespace view: single unified SVG canvas ──────── */
          <K8sMultiNsGraph
            multiGraphData={multiGraphData}
            humanitecEnvs={humanitecEnvs}
            deploymentName={deploymentName}
            loading={multiGraphLoading}
            error={multiGraphError}
            onPodClick={(pod, ns) => { setActiveNs(ns); handlePodClick(pod, ns); }}
            onRestartClick={ns => { setActiveNs(ns); setRestartResult(null); setRestartOpen(true); }}
            onEventsClick={ns => { setActiveNs(ns); setEventsData(null); setEventsOpen(true); fetchEvents(ns); }}
            onScaleClick={ns => { setActiveNs(ns); setScaleResult(null); setScaleOpen(true); }}
            onNodeInfo={info => { setNodeInfoData(info); setNodeInfoOpen(true); }}
            onManifestClick={fetchManifest}
          />
        ) : (
          /* ── Single-namespace view ────────────────────────────────── */
          <Box style={{ minHeight: 220 }}>
            {graphLoading && (
              <Box p={3} display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 220 }}>
                <CircularProgress />
              </Box>
            )}
            {!graphLoading && graphError && (
              <Box p={3} display="flex" justifyContent="center">
                <Typography style={{ color: theme.palette.error.main }}>Failed to load graph: {graphError}</Typography>
              </Box>
            )}
            {!graphLoading && !graphError && graphData && (
              <K8sGraphDiagram
                graphData={graphData}
                onPodClick={handlePodClick}
                onRestartClick={() => { setRestartResult(null); setRestartOpen(true); }}
                onEventsClick={() => { setEventsData(null); setEventsOpen(true); fetchEvents(); }}
                onScaleClick={() => { setScaleResult(null); setScaleOpen(true); }}
                onNodeInfo={info => { setNodeInfoData(info); setNodeInfoOpen(true); }}
                onManifestClick={fetchManifest}
              />
            )}
          </Box>
        )}
      </InfoCard>

      {/* ── Logs panel (slides up from bottom) ─────────────────────── */}
      <Drawer anchor="bottom" open={logsOpen} onClose={() => setLogsOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '60vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 12px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <DescriptionIcon style={{ opacity: 0.7 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Pod Logs{selectedPod ? ' — ' + selectedPod : ''}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {graphPods.length > 0 && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
                <InputLabel>Pod</InputLabel>
                <Select value={selectedPod}
                  onChange={e => {
                    const n = e.target.value as string;
                    const ns = activeNs || k8sNamespace;
                    setSelectedPod(n);
                    setSelectedPodNs(ns);
                    fetchLogs(n, ns);
                  }}
                  label="Pod">
                  {graphPods.map(p => (
                    <MenuItem key={p.name} value={p.name}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button size="small"
              onClick={() => selectedPod && fetchLogs(selectedPod, selectedPodNs || undefined)}
              disabled={logsFetching || !selectedPod}
              startIcon={logsFetching ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}>
              Refresh
            </Button>
            <IconButton size="small" onClick={() => setLogsOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </Box>
        <Box style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {logsFetching && !logsData && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          )}
          {logsError && (
            <Typography style={{ color: theme.palette.error.main }}>{logsError}</Typography>
          )}
          {logsData !== null && !logsFetching && (
            <pre style={{
              fontSize: '0.78rem', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              fontFamily: 'monospace', lineHeight: 1.6,
              backgroundColor: 'rgba(0,0,0,0.18)', padding: 16, borderRadius: 8,
            }}>
              {logsData || '(no log output)'}
            </pre>
          )}
        </Box>
      </Drawer>

      {/* ── Events panel ───────────────────────────────────────────── */}
      <Drawer anchor="bottom" open={eventsOpen} onClose={() => setEventsOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '60vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 12px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TimelineIcon style={{ opacity: 0.7 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>Kubernetes Events</Typography>
            {eventsData && !eventsFetching && (
              <Box style={{
                display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.8rem',
                backgroundColor: eventsData.some(e => e.type === 'Warning') ? clrError + '22' : clrOk + '22',
                color: eventsData.some(e => e.type === 'Warning') ? clrError : clrOk,
                border: '1px solid currentColor',
              }}>{eventsData.length}</Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button size="small" onClick={() => fetchEvents()} disabled={eventsFetching}
              startIcon={eventsFetching ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}>
              Refresh
            </Button>
            <IconButton size="small" onClick={() => setEventsOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </Box>
        <Box style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {eventsFetching && !eventsData && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          )}
          {eventsError && (
            <Typography style={{ color: theme.palette.error.main }}>{eventsError}</Typography>
          )}
          {eventsData && !eventsFetching && (
            eventsData.length === 0 ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic', padding: 8 }}>
                No events found for <strong>{deploymentName}</strong>.
              </Typography>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid ' + theme.palette.divider }}>
                    {['Type', 'Reason', '#', 'Last Seen', 'Message'].map(h => (
                      <th key={h} style={{
                        padding: '6px 10px', fontWeight: 600, opacity: 0.6,
                        textTransform: 'uppercase', fontSize: '0.72rem',
                        letterSpacing: '0.05em', whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eventsData.map((ev, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid ' + theme.palette.divider }}>
                      <td style={{ padding: '7px 10px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 10,
                          fontSize: '0.72rem',
                          backgroundColor: ev.type === 'Warning' ? clrError + '22' : clrOk + '22',
                          color: ev.type === 'Warning' ? clrError : clrOk,
                          border: '1px solid currentColor',
                        }}>{ev.type}</span>
                      </td>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {ev.reason}
                      </td>
                      <td style={{ padding: '7px 10px' }}>{ev.count ?? '\u2014'}</td>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', opacity: 0.8 }}>
                        {ev.lastTimestamp ? new Date(ev.lastTimestamp).toLocaleString() : '\u2014'}
                      </td>
                      <td style={{ padding: '7px 10px', maxWidth: 480, wordBreak: 'break-word' }}>
                        {ev.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
        </Box>
      </Drawer>

      {/* ── Restart panel ──────────────────────────────────────────── */}
      <Drawer anchor="bottom" open={restartOpen} onClose={() => !restartLoading && setRestartOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '32vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 16px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <PlayArrowIcon style={{ opacity: 0.7 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>Rolling Restart</Typography>
          </Box>
          <IconButton size="small" onClick={() => !restartLoading && setRestartOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box style={{ padding: 20 }}>
          <Typography variant="body1" gutterBottom>
            Trigger a rolling restart of <strong>{deploymentName}</strong> in namespace{' '}
            <strong>{activeNs}</strong>? Pods are replaced one at a time.
          </Typography>
          {restartResult && (
            <Typography className={restartResult.ok ? classes.resultText : classes.errorText}
              style={{ marginBottom: 12 }}>
              {restartResult.message}
              {restartResult.ok && restartResult.detail && ' \u2014 ' + restartResult.detail}
            </Typography>
          )}
          <Box display="flex" style={{ gap: 12, marginTop: 16 }}>
            <Button variant="outlined" onClick={() => setRestartOpen(false)} disabled={restartLoading}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" disabled={restartLoading}
              startIcon={restartLoading ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
              onClick={handleRestart}>
              {restartLoading ? 'Restarting\u2026' : 'Confirm Restart'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Scale panel ────────────────────────────────────────────── */}
      <Drawer anchor="bottom" open={scaleOpen} onClose={() => !scaleLoading && setScaleOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '36vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 16px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TuneIcon style={{ opacity: 0.7 }} />
            <Typography variant="h6" style={{ fontWeight: 700 }}>Scale Deployment</Typography>
          </Box>
          <IconButton size="small" onClick={() => !scaleLoading && setScaleOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box style={{ padding: 20 }}>
          {hasHumanitec ? (
            <Box style={{
              padding: '14px 18px', borderRadius: 8,
              border: '1px solid ' + theme.palette.divider,
              backgroundColor: theme.palette.background.default,
            }}>
              <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 4 }}>
                Managed by Humanitec
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Use the <strong>Orchestrator</strong> tab to scale this deployment.
                Changes made there persist across future deployments.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Set replica count for <strong>{deploymentName}</strong>:
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '12px 0 20px' }}>
                <Slider
                  className={classes.slider}
                  min={1} max={10} step={1}
                  value={scaleValue}
                  onChange={(_e, v) => setScaleValue(v as number)}
                  marks valueLabelDisplay="on"
                />
                <Typography variant="h5" className={classes.replicaBadge}>{scaleValue}</Typography>
              </Box>
              {scaleResult && (
                <Typography className={scaleResult.ok ? classes.resultText : classes.errorText}
                  style={{ marginBottom: 12 }}>
                  {scaleResult.message}
                </Typography>
              )}
              <Box display="flex" style={{ gap: 12 }}>
                <Button variant="outlined" onClick={() => setScaleOpen(false)} disabled={scaleLoading}>
                  Cancel
                </Button>
                <Button color="primary" variant="contained" disabled={scaleLoading}
                  startIcon={scaleLoading ? <CircularProgress size={16} /> : <TuneIcon fontSize="small" />}
                  onClick={handleScale}>
                  {scaleLoading ? 'Scaling\u2026' : 'Scale to ' + scaleValue + ' replica' + (scaleValue !== 1 ? 's' : '')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* ── Node Info panel ─────────────────────────────────── */}
      <Drawer anchor="bottom" open={nodeInfoOpen} onClose={() => setNodeInfoOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '50vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 16px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            {nodeInfoData ? nodeInfoData.kind + ' — ' + nodeInfoData.name : 'Info'}
          </Typography>
          <IconButton size="small" onClick={() => setNodeInfoOpen(false)}><CloseIcon /></IconButton>
        </Box>
        {nodeInfoData && (
          <Box style={{ padding: 20, overflowY: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {nodeInfoData.fields.map(({ label, value, mono }) => (
                  <tr key={label} style={{ borderBottom: '1px solid ' + theme.palette.divider }}>
                    <td style={{
                      padding: '10px 16px 10px 0', fontWeight: 600, opacity: 0.65,
                      textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.05em',
                      whiteSpace: 'nowrap', verticalAlign: 'top', width: 160,
                    }}>{label}</td>
                    <td style={{
                      padding: '10px 0', fontSize: '0.9rem',
                      fontFamily: mono ? 'monospace' : undefined,
                    }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Drawer>
      {/* ── Manifest viewer panel ──────────────────────────── */}
      <Drawer anchor="bottom" open={manifestOpen} onClose={() => setManifestOpen(false)}
        PaperProps={{ style: { ...drawerPaper, maxHeight: '70vh' } }}>
        {handle}
        <Box display="flex" justifyContent="space-between" alignItems="center"
          style={{ padding: '0 20px 16px', borderBottom: '1px solid ' + theme.palette.divider }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <span style={{ fontSize: 18, color: clrOk }}>&#9776;</span>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {manifestTitle || 'Manifest'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setManifestOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Box style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {manifestLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 120 }}>
              <CircularProgress />
            </Box>
          )}
          {manifestError && (
            <Typography style={{ color: theme.palette.error.main, fontFamily: 'monospace' }}>
              {manifestError}
            </Typography>
          )}
          {manifestData && (
            <pre style={{
              margin: 0, fontSize: '0.78rem', lineHeight: 1.55,
              fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              backgroundColor: theme.palette.background.default,
              padding: 16, borderRadius: 8,
              border: '1px solid ' + theme.palette.divider,
            }}>{manifestData}</pre>
          )}
        </Box>
      </Drawer>

    </>
  );
}
